<template>
    <div class="bg-white border border-grey-light rounded shadow overflow-hidden">
        <div class="flex justify-between items-center py-3 px-4 bg-grey-lighter border-b border-grey-light">
            <h2 class="text-lg font-semibold">Quiz</h2>
            <button
                v-if="quizState === 'taking'"
                class="text-sm text-grey-darker hover:text-grey-darkest"
                @click.prevent="isExpanded = !isExpanded">
                {{ isExpanded ? 'Hide -' : 'Show +' }}
            </button>
        </div>
        
        <!-- Taking Quiz State -->
        <div v-if="quizState === 'taking'" v-show="isExpanded" class="p-4 space-y-6">
            <div
                v-for="(question, questionIndex) in questions"
                :key="question.id"
                class="border-b border-grey-light pb-6 last:border-b-0 last:pb-0">
                
                <div class="mb-4">
                    <h3 class="font-semibold text-base mb-2">
                        {{ questionIndex + 1 }}. {{ question.title }}
                    </h3>
                </div>
                
                <div class="grid grid-cols-2 gap-4">
                    <div
                        v-for="(answer, answerIndex) in question.answers"
                        :key="answerIndex"
                        class="flex items-start">
                        <label class="flex items-center cursor-pointer">
                            <input
                                :type="isMSQ(question) ? 'checkbox' : 'radio'"
                                :name="`question-${question.id}`"
                                :value="answerIndex"
                                :checked="isAnswerSelected(question, answerIndex)"
                                class="mr-3"
                                @change="onAnswerChange(question, answerIndex, $event.target.checked)"/>
                            <span class="text-sm leading-relaxed">{{ answer.text }}</span>
                        </label>
                    </div>
                </div>
            </div>
            
            <div class="flex justify-end pt-4">
                <button
                    id="submit_quiz"
                    :disabled="!isQuizComplete"
                    :class="[
                        'text-white font-semibold py-2 px-6 rounded transition-colors',
                        isQuizComplete 
                            ? 'bg-red-600 hover:bg-red-700 cursor-pointer' 
                            : 'bg-grey-dark opacity-50 cursor-not-allowed'
                    ]"
                    @click.prevent="submitQuiz">
                    Submit
                </button>
            </div>
        </div>

        <div v-if="quizState === 'results'" class="p-4 space-y-6">
            <!-- Score Display -->
            <div class="flex justify-between items-center mb-6 pb-4 border-b border-grey-light">
                <h3 class="text-xl font-semibold">Score {{ correctCount }}/{{ totalQuestions }}</h3>
                <p  
                    v-if="isAllCorrect"
                    class="text-green-700 font-semibold text-lg text-center mt-3"
                >
                    Well done!
                </p>
                <p
                    v-else
                    class="text-red-600 font-semibold text-lg text-center mt-3">
                    Please try again!
                </p>
            </div>

            <div
                v-for="(question, questionIndex) in questions"
                :key="question.id"
                class="border-b border-grey-light pb-6 last:border-b-0 last:pb-0">
                
                <div class="mb-4">
                    <h3 class="font-semibold text-base mb-2">
                        {{ questionIndex + 1 }}. {{ question.title }}
                    </h3>
                </div>
                
                <!-- Show all answers with selected answer marked as right/wrong -->
                <div class="grid grid-cols-2 gap-4 mb-4">
                    <div
                        v-for="(answer, answerIndex) in question.answers"
                        :key="answerIndex"
                        class="flex items-start">
                        <label class="flex items-center w-full">
                            <input
                                :type="isMSQ(question) ? 'checkbox' : 'radio'"
                                :name="`question-${question.id}-result`"
                                :value="answerIndex"
                                :checked="isAnswerSelected(question, answerIndex)"
                                disabled
                                class="mr-3"/>
                            <span class="text-sm leading-relaxed flex items-center flex-1">
                                {{ answer.text }}
                                <span v-if="isAnswerSelected(question, answerIndex) && isAnswerCorrect(question)" class="ml-2 text-green-600">
                                    <font-awesome-icon icon="check" class="text-lg"/>
                                </span>
                                <span v-else-if="isAnswerSelected(question, answerIndex) && !isAnswerCorrect(question)" class="ml-2 text-red-600">
                                    <font-awesome-icon icon="times" class="text-lg"/>
                                </span>
                            </span>
                        </label>
                    </div>
                </div>

            </div>

            <!-- Action Buttons -->
            <div class="flex justify-end pt-4 gap-3">
                <button
                    class="bg-grey-dark hover:bg-grey-darker text-white font-semibold py-2 px-6 rounded transition-colors"
                    @click.prevent="resetQuiz">
                    Reset Quiz
                </button>
                <button
                    id="reveal_quiz_answer"
                    class="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded transition-colors"
                    @click.prevent="revealAnswers">
                    Reveal Answers
                </button>
            </div>

            <!-- ShareThis Inline Buttons (visible only after quiz completion) -->
            <div v-if="showInlineShareButtons" class="pt-2">
                <div
                    class="sharethis-inline-share-buttons"
                    :data-url="currentShareUrl"
                    :data-title="currentShareTitle">
                </div>
            </div>
        </div>

        <div v-if="quizState === 'reveal'" class="p-4 space-y-6">
            <!-- Score Display -->
            <div class="mb-6 pb-4 border-b border-grey-light">
                <h3 class="text-xl font-semibold">Score {{ correctCount }}/{{ totalQuestions }}</h3>
            </div>

            <div
                v-for="(question, questionIndex) in questions"
                :key="question.id"
                class="border-b border-grey-light pb-6 last:border-b-0 last:pb-0">
                
                <div class="mb-4">
                    <h3 class="font-semibold text-base mb-2">
                        {{ questionIndex + 1 }}. {{ question.title }}
                    </h3>
                </div>
                
                <!-- Show all answers with correct answer marked and wrong selected answer marked -->
                <div class="grid grid-cols-2 gap-4 mb-4">
                    <div
                        v-for="(answer, answerIndex) in question.answers"
                        :key="answerIndex"
                        class="flex items-start">
                        <label class="flex items-center w-full">
                            <input
                                :type="isMSQ(question) ? 'checkbox' : 'radio'"
                                :name="`question-${question.id}-reveal`"
                                :value="answerIndex"
                                :checked="isAnswerSelected(question, answerIndex)"
                                disabled
                                class="mr-3"/>
                            <span class="text-sm leading-relaxed flex items-center flex-1">
                                {{ answer.text }}
                                <span v-if="answer.is_correct" class="ml-2 text-green-600">
                                    <font-awesome-icon icon="check" class="text-lg"/>
                                </span>
                                <span v-else-if="isAnswerSelected(question, answerIndex)" class="ml-2 text-red-600">
                                    <font-awesome-icon icon="times" class="text-lg"/>
                                </span>
                            </span>
                        </label>
                    </div>
                </div>

                <!-- Explanation -->
                <div v-if="hasExplanation(question)" class="mt-4 p-4 bg-grey-lighter rounded border border-grey-light">
                    <h4 class="font-semibold text-sm mb-2">Explanation</h4>
                    <div
                        class="prose prose-sm max-w-none text-grey-darkest"
                        v-html="question.explanation"
                        @click="handleExplanationClick"/>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex justify-end pt-4">
                <button
                    class="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded transition-colors"
                    @click.prevent="resetQuiz">
                    Reset Quiz
                </button>
            </div>

            <!-- ShareThis Inline Buttons (visible only after quiz completion) -->
            <div v-if="showInlineShareButtons" class="pt-2">
                <div
                    class="sharethis-inline-share-buttons"
                    :data-url="currentShareUrl"
                    :data-title="currentShareTitle">
                </div>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    name: 'Quiz',
    
    props: {
        questions: {
            type: Array,
            required: true,
            default: () => []
        },
        postTitle: {
            type: String,
            default: ''
        }
    },
    
    data() {
        return {
            isExpanded: true,
            selectedAnswers: {},
            quizState: 'taking',
            correctCount: 0,
            totalQuestions: 0
        };
    },
    
    computed: {
        isQuizComplete() {
            // Check if all questions have been answered
            return this.questions.every(question => this.isQuestionAnswered(question));
        },

        isAllCorrect() {
            return this.totalQuestions > 0 && this.correctCount === this.totalQuestions;
        },
        showInlineShareButtons() {
            return this.quizState === 'results' || this.quizState === 'reveal';
        },
        currentShareUrl() {
            return window.location.href;
        },
        currentShareTitle() {
            return document.title || 'MMCTS';
        }
    },
    
    mounted() {
        // Validate correctness metadata for each question
        this.questions.forEach((question, index) => {
            const correctAnswers = question.answers.filter(answer => answer.is_correct);
            if (correctAnswers.length < 1) {
                console.error(`Question ${index + 1} (ID: ${question.id}) has no correct answers.`);
            }
            if (!this.isMSQ(question) && correctAnswers.length !== 1) {
                console.error(`Question ${index + 1} (ID: ${question.id}) has ${correctAnswers.length} correct answers; MCQ expects exactly one.`);
            }
        });
        
        this.totalQuestions = this.questions.length;
    },
    
    methods: {
        ensureInlineShareScript() {
            return new Promise((resolve) => {
                const inlineScriptId = 'sharethis-inline-share-buttons-script';
                const existing = document.getElementById(inlineScriptId);

                if (existing) {
                    resolve();
                    return;
                }

                const script = document.createElement('script');
                script.id = inlineScriptId;
                script.type = 'text/javascript';
                script.src = 'https://platform-api.sharethis.com/js/sharethis.js#property=69dc99738464330f2e14d33a&product=inline-share-buttons';
                script.async = true;
                script.onload = () => resolve();
                script.onerror = () => resolve();
                document.head.appendChild(script);
            });
        },
        renderInlineShareButtons() {
            this.$nextTick(() => {
                this.ensureInlineShareScript().then(() => {
                    setTimeout(() => {
                        if (window.__sharethis__ && typeof window.__sharethis__.initialize === 'function') {
                            window.__sharethis__.initialize();
                        }
                        if (window.__sharethis__ && typeof window.__sharethis__.load === 'function') {
                            window.__sharethis__.load('inline-share-buttons');
                        }
                    }, 180);
                });
            });
        },
        isMSQ(question) {
            return question.question_type === 'msq';
        },
        isAnswerSelected(question, answerIndex) {
            const selected = this.selectedAnswers[question.id];
            if (this.isMSQ(question)) {
                return Array.isArray(selected) && selected.includes(answerIndex);
            }
            return selected === answerIndex;
        },
        isQuestionAnswered(question) {
            const selected = this.selectedAnswers[question.id];
            if (this.isMSQ(question)) {
                return Array.isArray(selected) && selected.length > 0;
            }
            return selected !== undefined && selected !== null;
        },
        getCorrectAnswerIndexes(question) {
            return question.answers
                .map((answer, index) => answer.is_correct ? index : null)
                .filter(index => index !== null);
        },
        getSelectedAnswerIndexes(question) {
            const selected = this.selectedAnswers[question.id];
            if (this.isMSQ(question)) {
                return Array.isArray(selected) ? _.sortBy(selected) : [];
            }
            return selected !== undefined && selected !== null ? [selected] : [];
        },
        onAnswerChange(question, answerIndex, checked) {
            if (this.isMSQ(question)) {
                const selected = Array.isArray(this.selectedAnswers[question.id])
                    ? [...this.selectedAnswers[question.id]]
                    : [];
                if (checked && !selected.includes(answerIndex)) {
                    selected.push(answerIndex);
                }
                if (!checked) {
                    const selectedIndex = selected.indexOf(answerIndex);
                    if (selectedIndex !== -1) {
                        selected.splice(selectedIndex, 1);
                    }
                }
                this.$set(this.selectedAnswers, question.id, _.sortBy(selected));
                return;
            }

            // Single select - only one answer can be selected per question
            this.$set(this.selectedAnswers, question.id, answerIndex);
        },
        
        submitQuiz() {
            // Validate that all questions are answered
            const unansweredQuestions = this.questions.filter(q => !this.isQuestionAnswered(q));
            
            if (unansweredQuestions.length > 0) {
                alert(`Please answer all questions before submitting.`);
                return;
            }
            
            // Calculate score
            let correctCount = 0;
            this.questions.forEach(question => {
                const correctIndexes = this.getCorrectAnswerIndexes(question);
                if (correctIndexes.length < 1) {
                    return;
                }

                if (this.isMSQ(question)) {
                    const selectedIndexes = this.getSelectedAnswerIndexes(question);
                    const isFullyCorrect = selectedIndexes.length === correctIndexes.length
                        && selectedIndexes.every(index => correctIndexes.includes(index));
                    if (isFullyCorrect) {
                        correctCount++;
                    }
                } else if (this.selectedAnswers[question.id] === correctIndexes[0]) {
                    correctCount++;
                }
            });
            
            this.correctCount = correctCount;
            // Push quiz_completed event to Google Analytics dataLayer
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'quiz_completed',
                quiz_score: correctCount,
                quiz_post_title: this.postTitle || this.currentShareTitle
            });
            // Go to results state after submit
            this.quizState = 'results';
            this.renderInlineShareButtons();
        },
        
        revealAnswers() {
            // Go to reveal answers state
            this.quizState = 'reveal';
            this.renderInlineShareButtons();
        },
        
        resetQuiz() {
            this.selectedAnswers = {};
            this.correctCount = 0;
            this.isExpanded = true;
            this.quizState = 'taking';
        },
        
        isAnswerCorrect(question) {
            const correctIndexes = this.getCorrectAnswerIndexes(question);
            if (this.isMSQ(question)) {
                const selectedIndexes = this.getSelectedAnswerIndexes(question);
                return selectedIndexes.length === correctIndexes.length
                    && selectedIndexes.every(index => correctIndexes.includes(index));
            }
            return this.selectedAnswers[question.id] === correctIndexes[0];
        },
        
        getSelectedAnswerText(question) {
            const selectedAnswerIndex = this.selectedAnswers[question.id];
            if (selectedAnswerIndex !== undefined && selectedAnswerIndex !== null) {
                return question.answers[selectedAnswerIndex]?.text || '';
            }
            return '';
        },
        
        hasExplanation(question) {
            const explanation = question.explanation;
            if (!explanation || !explanation.trim()) {
                return false;
            }

            return explanation.replace(/<[^>]*>/g, '').trim().length > 0;
        },
        handleExplanationClick(event) {
            const timestampElement = event.target.closest('[data-video-time]');

            if (!timestampElement) {
                return;
            }

            const seconds = parseInt(timestampElement.dataset.videoTime, 10);

            if (Number.isNaN(seconds)) {
                return;
            }

            event.preventDefault();
            this.$emit('seek-video', seconds);
        },
    }
};
</script>

<style scoped>
input[type="radio"],
input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
}

input[type="radio"]:disabled,
input[type="checkbox"]:disabled {
    cursor: not-allowed;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/deep/ .video-timestamp {
    cursor: pointer;
}
</style>
